import React, { useEffect, useState } from "react";
import { useAuth } from "@/admin/AuthContext";
import { Loader2, Upload, Copy, Trash2, Search } from "lucide-react";
import { resolveImageUrl as toAbsoluteUrl } from "@/lib/media";
import { toast } from "sonner";

const humanSize = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};

const MediaLibraryPanel = () => {
  const { authAxios } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = () => {
    setLoading(true);
    authAxios
      .get("/admin/uploads/library?limit=500")
      .then((r) => setItems(r.data?.items || []))
      .catch(() => toast.error("Gagal memuat media"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        await authAxios.post("/admin/uploads/image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        ok++;
      } catch (err) {
        toast.error(`${file.name}: ${err?.response?.data?.detail || "gagal"}`);
      }
    }
    setUploading(false);
    if (ok) {
      toast.success(`${ok} file berhasil di-upload`);
      load();
    }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("URL disalin");
    } catch {
      toast.error("Gagal menyalin");
    }
  };

  const del = async (filename) => {
    if (!window.confirm(`Hapus "${filename}" secara permanen?`)) return;
    try {
      await authAxios.delete(`/admin/uploads/${encodeURIComponent(filename)}`);
      setItems((cur) => cur.filter((x) => x.filename !== filename));
      toast.success("File dihapus");
    } catch {
      toast.error("Gagal menghapus");
    }
  };

  const filtered = q
    ? items.filter((it) => it.filename.toLowerCase().includes(q.toLowerCase()))
    : items;

  return (
    <div data-testid="admin-media-library-panel">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Media Library</h2>
          <p className="text-sm text-slate-500 mt-1">
            {items.length} file tersimpan di <code>/api/assets/</code> — total{" "}
            {humanSize(items.reduce((s, i) => s + i.size, 0))}
          </p>
        </div>
        <label
          data-testid="admin-media-upload-btn"
          className="inline-flex items-center gap-2 bg-blue-800 hover:bg-blue-900 text-white text-sm font-medium px-4 py-2.5 rounded-full shadow-md shadow-blue-900/20 cursor-pointer"
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          {uploading ? "Mengunggah..." : "Upload File"}
          <input
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={(e) => handleUpload(Array.from(e.target.files || []))}
          />
        </label>
      </div>

      <div className="mb-4 relative max-w-md">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cari nama file..."
          data-testid="admin-media-search"
          className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
        />
      </div>

      {loading ? (
        <div className="p-12 flex items-center justify-center text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="p-12 text-center text-slate-400 text-sm border border-dashed border-slate-200 rounded-2xl">
          {q ? "Tidak ada file cocok" : "Belum ada file — klik Upload File untuk mulai."}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {filtered.map((it) => (
            <div
              key={it.filename}
              data-testid={`admin-media-item-${it.filename}`}
              className="group relative bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-blue-500 hover:shadow-md transition"
            >
              <div className="aspect-square bg-slate-50">
                <img
                  src={toAbsoluteUrl(it.url)}
                  alt={it.filename}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-2 border-t border-slate-100">
                <div className="text-[10px] font-mono text-slate-500 truncate" title={it.filename}>
                  {it.filename}
                </div>
                <div className="text-[10px] text-slate-400 flex justify-between">
                  <span>{humanSize(it.size)}</span>
                  <span>{new Date(it.modified_at).toLocaleDateString("id-ID")}</span>
                </div>
              </div>
              <div className="absolute inset-x-0 bottom-0 opacity-0 group-hover:opacity-100 transition flex bg-white/95 border-t border-slate-100">
                <button
                  onClick={() => copy(it.url)}
                  data-testid={`admin-media-copy-${it.filename}`}
                  className="flex-1 py-1.5 text-[11px] text-blue-800 hover:bg-blue-50 flex items-center justify-center gap-1"
                >
                  <Copy className="h-3 w-3" /> Copy URL
                </button>
                <button
                  onClick={() => del(it.filename)}
                  data-testid={`admin-media-delete-${it.filename}`}
                  className="flex-1 py-1.5 text-[11px] text-red-700 hover:bg-red-50 flex items-center justify-center gap-1 border-l border-slate-100"
                >
                  <Trash2 className="h-3 w-3" /> Hapus
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MediaLibraryPanel;
